import type { CalculationInfoResponse } from '@kk/api/modules/customerPortal';
import { capitalizeFirstLetter } from '@kk/shared/utils/strings';
import Icon from '@kk/ui/components/Icon';
import Tooltip from '@kk/ui/components/Tooltip';
import { usePriceInfoRows } from '@kk/ui/hooks/usePriceInfoRows';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

export interface PriceInfoProps {
  calculation: CalculationInfoResponse;
}

export function PriceInfo(props: PriceInfoProps) {
  const { t } = useTranslation('ui');
  const { calculation } = props;

  const infoRows = usePriceInfoRows(calculation);

  return (
    <div className="screen:bg-blue-5 screen:p-6 rounded-md screen:border border-blue-10 grid gap-y-6 content-start h-full">
      <h5 className="typ-headline-md">{t('loan.priceInformation')}</h5>
      <dl className="grid grid-cols-2 gap-y-3 typ-body-sm items-baseline print:gap-x-4">
        {infoRows.map(({ id, label, value, tooltip }) => {
          const tooltipId = tooltip ? `${id}-tooltip` : undefined;
          return (
            <Fragment key={label}>
              <dt data-testid={`${id}-label`} aria-describedby={tooltipId}>
                <span className="flex items-center gap-x-1 print:wrap-break-word">
                  {capitalizeFirstLetter(label)}
                  {tooltip ? (
                    <>
                      <div className="print:inline hidden print:text-xs">
                        ({tooltip})
                      </div>
                      <Tooltip
                        id={tooltipId}
                        tooltip={tooltip}
                        offsetOptions={{ mainAxis: 0 }}
                      >
                        <Icon
                          type="info"
                          size="tiny"
                          aria-hidden="true"
                          className="print:hidden"
                        />
                      </Tooltip>
                    </>
                  ) : null}
                </span>
              </dt>
              <dd
                className="place-self-end print:wrap-break-word print:text-right"
                data-testid={`${id}-value`}
              >
                {value}
              </dd>
            </Fragment>
          );
        })}
      </dl>
    </div>
  );
}
